import React, { useState } from 'react';
import { useStrategyOperations } from '../hooks/useStrategyOperations';
import { StrategyConfig } from '../types';
import { Link } from 'react-router-dom';

const StrategyConfigPage: React.FC = () => {
  const { config, loading, error, updateConfig, runStrategy } = useStrategyOperations();
  const [localConfig, setLocalConfig] = useState<StrategyConfig>(config);
  const [strategyComplete, setStrategyComplete] = useState<boolean>(false);

  const handleCapeYearsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value) as 8 | 9 | 10;
    setLocalConfig(prev => ({ ...prev, capeLookbackYears: value }));
    setStrategyComplete(false);
  };

  const handlePatternTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const mappedValue = value === 'triple' ? 'triple-top' : 'quad-top';
    setLocalConfig(prev => ({ ...prev, pnfPatternType: mappedValue }));
    setStrategyComplete(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLocalConfig(prev => ({
      ...prev,
      [name]: name === 'pnfBoxSizePercent' ? parseFloat(value) : parseInt(value),
    }));
    setStrategyComplete(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    updateConfig(localConfig);
    await runStrategy();
    setStrategyComplete(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Strategy Configuration</h1>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-800/50 border-l-4 border-red-400 dark:border-red-500 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400 dark:text-red-200" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-white font-medium">{error}</p>
            </div>
          </div>
        </div>
      )}

      {strategyComplete && (
        <div className="bg-green-50 dark:bg-green-800/50 border-l-4 border-green-400 dark:border-green-500 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400 dark:text-green-200" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700 dark:text-white font-medium">
                Strategy run completed successfully! 
                <Link to="/reports" className="ml-2 font-medium text-green-700 dark:text-white underline hover:text-green-800 dark:hover:text-green-100">
                  View Results
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">CAPE Ratio Configuration</h2>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="capeYears" className="label">CAPE Lookback Period</label>
                  <select
                    id="capeYears"
                    name="capeLookbackYears"
                    value={localConfig.capeLookbackYears}
                    onChange={handleCapeYearsChange}
                    className="input block w-full"
                  >
                    <option value={8}>8 Years (32 Quarters)</option>
                    <option value={9}>9 Years (36 Quarters)</option>
                    <option value={10}>10 Years (40 Quarters)</option>
                  </select>
                  <p className="mt-1 text-sm text-gray-500">Number of years of data to use for CAPE calculation</p>
                </div>
                <div>
                  <label htmlFor="capeEmaSpan" className="label">CAPE EMA Span</label>
                  <input
                    type="number"
                    name="capeEmaSpan"
                    id="capeEmaSpan"
                    min="1"
                    max="12"
                    value={localConfig.capeEmaSpan}
                    onChange={handleInputChange}
                    className="input block w-full"
                  />
                  <p className="mt-1 text-sm text-gray-500">Number of quarters for exponential moving average calculation</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Point & Figure Chart Configuration</h2>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-3">
                <div>
                  <label htmlFor="pnfPatternType" className="label">Pattern Type</label>
                  <select
                    id="pnfPatternType"
                    name="pnfPatternType"
                    value={localConfig.pnfPatternType === 'triple-top' ? 'triple' : 'quad'}
                    onChange={handlePatternTypeChange}
                    className="input block w-full"
                  >
                    <option value="triple">Triple Top/Bottom</option>
                    <option value="quad">Quad Top/Bottom</option>
                  </select>
                  <p className="mt-1 text-sm text-gray-500">Type of pattern to detect on P&F charts</p>
                </div>
                <div>
                  <label htmlFor="pnfBoxSizePercent" className="label">Box Size (%)</label>
                  <input
                    type="number"
                    name="pnfBoxSizePercent"
                    id="pnfBoxSizePercent"
                    min="0.001"
                    max="0.05"
                    step="0.001"
                    value={localConfig.pnfBoxSizePercent}
                    onChange={handleInputChange}
                    className="input block w-full"
                  />
                  <p className="mt-1 text-sm text-gray-500">Box size as a percentage of price</p>
                </div>
                <div>
                  <label htmlFor="pnfReversalBoxes" className="label">Reversal Boxes</label>
                  <input
                    type="number"
                    name="pnfReversalBoxes"
                    id="pnfReversalBoxes"
                    min="1"
                    max="10"
                    value={localConfig.pnfReversalBoxes}
                    onChange={handleInputChange}
                    className="input block w-full"
                  />
                  <p className="mt-1 text-sm text-gray-500">Number of boxes required for a reversal</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setLocalConfig(config)}
                className="btn bg-default border border-gray-300 hover:bg-hover"
                disabled={loading}
              >
                Reset
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Running Strategy...
                  </span>
                ) : (
                  'Run Strategy'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className="card">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Strategy Parameter Explanation</h2>
        <div className="space-y-4">
          <div>
            <h3 className="text-md font-medium text-gray-900">CAPE Ratio (Cyclically Adjusted Price-to-Earnings)</h3>
            <p className="text-sm text-gray-500">
              The CAPE ratio calculates a company's price relative to its average earnings over a period of years, 
              adjusted for inflation. This helps smooth out short-term earnings fluctuations and provides a more 
              stable measure of valuation.
            </p>
          </div>
          <div>
            <h3 className="text-md font-medium text-gray-900">Piotroski F-Score</h3>
            <p className="text-sm text-gray-500">
              The F-Score uses nine binary criteria to assess a company's financial health based on profitability, 
              leverage/liquidity, and operating efficiency. A score of 8-9 indicates strong financial health, 
              while 0-2 suggests potential weakness.
            </p>
          </div>
          <div>
            <h3 className="text-md font-medium text-gray-900">Point & Figure Charts</h3>
            <p className="text-sm text-gray-500">
              P&F charts filter out minor price movements and focus on significant changes, creating columns of X's (rising prices) 
              and O's (falling prices). Triple/Quad top patterns occur when price makes three/four equal highs followed by a 
              breakout or breakdown, signaling potential trend changes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategyConfigPage; 